import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, Col, Row } from 'react-bootstrap';
import CountUp from 'react-countup';
import useToken from 'hooks/useToken';
import { StatsCardSkeleton } from './SkeletonLoader';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faHourglassHalf,
    faCheckCircle,
    faDatabase,
    faBolt,
    faLink
} from '@fortawesome/free-solid-svg-icons';

const StatCard = ({ title, value, subValue, subLabel, color, icon, isCurrency }) => (
    <Card className="h-100 shadow-sm border-0">
        <Card.Body>
            <div className="d-flex justify-content-between align-items-start">
                <div className="flex-grow-1">
                    <p className="text-muted mb-2 text-uppercase small fw-semibold">{title}</p>
                    <h4 className={`text-${color} mb-0 fw-bold text-nowrap`}>
                        {isCurrency ? (
                            <>
                                $<CountUp end={value || 0} duration={1.5} separator="," decimals={2} decimal="." />
                            </>
                        ) : (
                            <CountUp end={value || 0} duration={1.5} separator="," />
                        )}
                    </h4>
                    {subValue !== undefined && subValue !== null && (
                        <div className={`text-${color} small mt-1 opacity-75 fw-bold`}>
                            <span className="me-1">+</span>
                            <CountUp end={subValue} duration={1.5} separator="," />
                            <span className="ms-1">{subLabel || ''}</span>
                        </div>
                    )}
                </div>
                {icon && (
                    <div className={`bg-${color} bg-opacity-10 p-3 rounded-3 d-flex align-items-center justify-content-center`}
                        style={{ width: '56px', height: '56px', minWidth: '56px' }}>
                        <FontAwesomeIcon icon={icon} className={`text-${color}`} style={{ fontSize: '24px' }} />
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>
);

const DualStatCard = ({ title1, value1, title2, value2, color, icon }) => (
    <Card className="h-100 shadow-sm border-0">
        <Card.Body className="py-2 px-3">
            <div className="d-flex justify-content-between align-items-center h-100">
                <div className="d-flex flex-column justify-content-center flex-grow-1">
                    {/* Row 1 */}
                    <div className="mb-1">
                        <p className="text-muted mb-0 text-uppercase fw-bold text-nowrap" style={{ fontSize: '0.65rem' }}>{title1}</p>
                        <h4 className={`text-${color} mb-0 fw-bold lh-1`}>
                            <CountUp end={value1 || 0} duration={1.5} separator="," />
                        </h4>
                    </div>

                    {/* Divider */}
                    <div className="border-top my-1 w-75"></div>

                    {/* Row 2 */}
                    <div className="mt-1">
                        <p className="text-muted mb-0 text-uppercase fw-bold text-nowrap" style={{ fontSize: '0.65rem' }}>{title2}</p>
                        <h5 className="text-dark mb-0 fw-bold lh-1">
                            <span className={`text-${color} me-1 small`}>+</span>
                            <CountUp end={value2 || 0} duration={1.5} separator="," />
                        </h5>
                    </div>
                </div>

                {/* Single Icon on Right */}
                {icon && (
                    <div className={`bg-${color} bg-opacity-10 p-2 rounded-3 d-flex align-items-center justify-content-center ms-2`}
                        style={{ width: '48px', height: '48px', minWidth: '48px' }}>
                        <FontAwesomeIcon icon={icon} className={`text-${color}`} style={{ fontSize: '20px' }} />
                    </div>
                )}
            </div>
        </Card.Body>
    </Card>
);

DualStatCard.propTypes = {
    title1: PropTypes.string.isRequired,
    value1: PropTypes.number,
    title2: PropTypes.string.isRequired,
    value2: PropTypes.number,
    color: PropTypes.string.isRequired,
    icon: PropTypes.object
};

StatCard.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.number,
    subValue: PropTypes.number,
    subLabel: PropTypes.string,
    color: PropTypes.string.isRequired,
    icon: PropTypes.object,
    isCurrency: PropTypes.bool
};

const AkasiaPosStatsCards = () => {
    const { token } = useToken();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;

        const fetchStats = async () => {
            try {
                setLoading(true);

                const response = await fetch('/api/AkasiaPosDashboard/GetLicenseStatsCards', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({})
                });

                const result = await response.json();

                if (result.iResponseType === 1 && result.detalles) {
                    const parsed = JSON.parse(result.detalles);
                    const statsData = parsed.table2?.[0] || parsed.table1?.[0];
                    setStats(statsData);
                } else {
                    setError('Error loading stats');
                }
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchStats();
    }, [token]); // Re-fetch when token becomes available

    if (loading) {
        return <StatsCardSkeleton />;
    }

    if (error || !stats) {
        return (
            <div className="text-center py-5">
                <i className="bi bi-exclamation-circle text-danger fs-1"></i>
                <p className="text-danger mt-2">{error || 'No se pudieron cargar las estadísticas'}</p>
            </div>
        );
    }

    return (
        <Row className="g-3 mb-3">
            <Col xs={4} md={2} sm={2} >
                <StatCard
                    title="Ventas Totales"
                    value={stats.dVentasTotales}
                    color="success"
                    isCurrency={true}
                />
            </Col>
            <Col xs={4} md={2} sm={2}>
                <StatCard
                    title="Activas Hoy"
                    value={stats.iLicenciasConActividadHoy}
                    color="primary"
                    icon={faBolt}
                />
            </Col>
            <Col xs={4} md={2} sm={2}>
                <StatCard
                    title="Vinculadas"
                    value={stats.iLicenciasVinculadas}
                    color="secondary"
                    icon={faLink}
                />
            </Col>
            <Col xs={4} md={2} sm={2}>
                <StatCard
                    title="Pagadas Activas"
                    value={stats.iLicenciasCompradasActivas}
                    color="info"
                    icon={faCheckCircle}
                />
            </Col>
            <Col xs={4} md={2} sm={2}>
                <DualStatCard
                    title1="Pruebas"
                    value1={stats.iLicenciasPruebaActivas}
                    title2="Hoy"
                    value2={stats.iLicenciasPruebaNuevasHoy}
                    color="warning"
                    icon={faHourglassHalf}
                />
            </Col>
            <Col xs={4} md={2} sm={2}>
                <StatCard
                    title="Total Licencias"
                    value={stats.iLicenciasTotal}
                    color="dark"
                    icon={faDatabase}
                />
            </Col>
        </Row>
    );
};

export default AkasiaPosStatsCards;
